import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiClient, getUseVisitLogKey, getUseVisitLogsKey } from '@/hooks'
import { APIErrorResponse, FieldReport } from '@/types'
import { unwrapErrorResponse, unwrapResponse } from '@/utils'

export type UseAiValidateFieldReportProps = {
  id: number
}

/**
 * Hook for validating field reports with AI
 * @param id - The ID of the field report to validate
 * @returns A mutation result for AI validation
 */
export const useAiValidateFieldReport = ({
  id,
}: UseAiValidateFieldReportProps): UseMutationResult<
  FieldReport,
  APIErrorResponse,
  void
> => {
  const client = useApiClient()
  const queryClient = useQueryClient()

  const visitLogsListKey = getUseVisitLogsKey()
  const visitLogItemKey = getUseVisitLogKey(id)

  return useMutation(
    () =>
      client
        .post(`field_reports/${id}/ai_validate/`)
        .then(unwrapResponse)
        .catch(unwrapErrorResponse),
    {
      onSuccess: () => {
        // Invalidate related queries to refresh the data
        queryClient.invalidateQueries(visitLogsListKey)
        queryClient.invalidateQueries(visitLogItemKey)
      },
    }
  )
}
